import { useEffect, useState } from "react";
import { Save, Plus, Trash2, Factory, X, PencilLine, Building2, AlertTriangle, Download, Upload, Database, ChevronDown, ChevronRight, Search, Copy } from "lucide-react";
import { toast } from "sonner";
import {
  listScreenTypes,
  updateScreenType,
  deleteScreenType,
  createScreenType,
  listColorOptions,
  createColorOption,
  updateColorOption,
  deleteColorOption,
  getAppSettings,
  updateAppSettings,
  getBackupPreview,
  downloadBackupBlob,
  restoreBackup,
} from "@/lib/api";
import ScreenSketch from "@/components/ScreenSketch";

const SOURCES = [
  { value: "width", label: "opening width" },
  { value: "height", label: "opening height" },
  { value: "width/2", label: "opening width / 2" },
  { value: "height/2", label: "opening height / 2" },
  { value: "leaf_width", label: "leaf width" },
  { value: "leaf_width/2", label: "leaf width / 2" },
  { value: "track_width", label: "track width" },
];

const newTier = () => ({ width_mm: "", height_mm: "", price: "" });
const newPiece = () => ({
  label: "",
  qty: "1",
  source: "width",
  offset_mm: "0",
});

const emptyDraft = () => ({ tiers: [], cut_pieces: [] });

const BACKUP_KEY = "mosquitonick.last_backup_at";
const BACKUP_REMIND_DAYS = 14;

const useBackupReminder = () => {
  const [lastAt, setLastAt] = useState(() => {
    try {
      return localStorage.getItem(BACKUP_KEY) || "";
    } catch {
      return "";
    }
  });
  const stampNow = () => {
    const now = new Date().toISOString();
    try {
      localStorage.setItem(BACKUP_KEY, now);
    } catch {}
    setLastAt(now);
  };
  const daysSince = (() => {
    if (!lastAt) return null;
    const t = Date.parse(lastAt);
    if (isNaN(t)) return null;
    return Math.floor((Date.now() - t) / 86400000);
  })();
  const overdue =
    daysSince === null || daysSince >= BACKUP_REMIND_DAYS;
  return { lastAt, daysSince, overdue, stampNow };
};

const ColorOptionsCard = ({ options, onChange }) => {
  const [draft, setDraft] = useState({ label: "", multiplier_pct: "" });
  const [pendingPct, setPendingPct] = useState({}); // key -> string while typing

  const updatePctLocal = (key, value) => {
    setPendingPct((prev) => ({ ...prev, [key]: value }));
  };

  const commitPct = async (opt) => {
    const raw = pendingPct[opt.key];
    if (raw === undefined) return;
    const next = Number(raw);
    if (!Number.isFinite(next) || next < 0) {
      toast.error("Multiplier must be 0 or higher");
      return;
    }
    if (next === opt.multiplier_pct) {
      setPendingPct((prev) => {
        const { [opt.key]: _gone, ...rest } = prev;
        return rest;
      });
      return;
    }
    try {
      const fresh = await updateColorOption(opt.key, { multiplier_pct: next });
      onChange(options.map((o) => (o.key === opt.key ? fresh : o)));
      toast.success(`${opt.label} set to +${next}%`);
    } catch {
      toast.error("Could not save");
    } finally {
      setPendingPct((prev) => {
        const { [opt.key]: _gone, ...rest } = prev;
        return rest;
      });
    }
  };

  const renameOpt = async (opt, newLabel) => {
    const trimmed = (newLabel || "").trim();
    if (!trimmed || trimmed === opt.label) return;
    try {
      const fresh = await updateColorOption(opt.key, { label: trimmed });
      onChange(options.map((o) => (o.key === opt.key ? fresh : o)));
    } catch {
      toast.error("Rename failed");
    }
  };

  const removeOpt = async (opt) => {
    if (opt.key === "white") {
      toast.error("White is the baseline — cannot delete");
      return;
    }
    if (!window.confirm(`Delete colour option "${opt.label}"?`)) return;
    try {
      await deleteColorOption(opt.key);
      onChange(options.filter((o) => o.key !== opt.key));
    } catch {
      toast.error("Delete failed");
    }
  };

  const addOpt = async () => {
    const label = draft.label.trim();
    if (!label) {
      toast.error("Label required");
      return;
    }
    const pct = Number(draft.multiplier_pct);
    if (!Number.isFinite(pct) || pct < 0) {
      toast.error("Multiplier must be 0 or higher");
      return;
    }
    try {
      const created = await createColorOption(label, pct);
      onChange([...options, created]);
      setDraft({ label: "", multiplier_pct: "" });
    } catch {
      toast.error("Could not create");
    }
  };

  return (
    <div className="border border-zinc-200" data-testid="color-options-card">
      <div className="flex items-center justify-between border-b border-zinc-200 bg-[#f4f4f5] px-5 py-3">
        <div className="flex items-center gap-3">
          <PencilLine size={18} />
          <div>
            <div className="font-display text-xl font-black tracking-tight">
              Colour pricing
            </div>
            <div className="label-xs mt-1">
              Each room can be quoted in a colour — the % is added on top of the base tier price.
            </div>
          </div>
        </div>
      </div>
      <div className="p-5 space-y-3">
        {options.length === 0 ? (
          <div className="text-sm text-zinc-500 font-mono">No colour options yet.</div>
        ) : (
          <div className="border border-zinc-200">
            <div className="grid grid-cols-[1fr_120px_36px] gap-0 bg-[#f9f9fb] border-b border-zinc-200">
              <div className="px-3 py-2 label-xs">Label</div>
              <div className="px-3 py-2 label-xs">Multiplier %</div>
              <div className="px-3 py-2"></div>
            </div>
            {options.map((opt) => (
              <div
                key={opt.key}
                className="grid grid-cols-[1fr_120px_36px] gap-0 border-b border-zinc-100 last:border-b-0 items-stretch"
                data-testid={`color-option-row-${opt.key}`}
              >
                <input
                  defaultValue={opt.label}
                  onBlur={(e) => renameOpt(opt, e.target.value)}
                  className="px-3 py-2 text-sm border-0 focus:outline-none focus:bg-yellow-50"
                  data-testid={`color-option-label-${opt.key}`}
                />
                <div className="flex items-center gap-1 px-3 py-2 border-l border-zinc-100">
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={pendingPct[opt.key] ?? opt.multiplier_pct}
                    onChange={(e) => updatePctLocal(opt.key, e.target.value)}
                    onBlur={() => commitPct(opt)}
                    className="w-16 text-right tabular-nums border-0 focus:outline-none focus:bg-yellow-50"
                    data-testid={`color-option-pct-${opt.key}`}
                  />
                  <span className="text-zinc-400 text-xs">%</span>
                </div>
                <button
                  type="button"
                  onClick={() => removeOpt(opt)}
                  disabled={opt.key === "white"}
                  className="border-l border-zinc-100 text-zinc-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-zinc-400 flex items-center justify-center"
                  title={opt.key === "white" ? "White is the baseline" : "Delete"}
                  data-testid={`color-option-delete-${opt.key}`}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="border border-dashed border-zinc-300 p-3 grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-2 items-end">
          <div>
            <div className="label-xs mb-1">New colour label</div>
            <input
              className="input-sharp"
              placeholder="e.g. Bronze metallic"
              value={draft.label}
              onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
              data-testid="new-color-label-input"
            />
          </div>
          <div>
            <div className="label-xs mb-1">% markup</div>
            <input
              type="number"
              min="0"
              step="0.5"
              className="input-sharp tabular-nums"
              placeholder="0"
              value={draft.multiplier_pct}
              onChange={(e) => setDraft((d) => ({ ...d, multiplier_pct: e.target.value }))}
              data-testid="new-color-pct-input"
            />
          </div>
          <button
            type="button"
            onClick={addOpt}
            className="btn-sharp secondary"
            data-testid="add-color-option-btn"
          >
            <Plus size={13} /> Add
          </button>
        </div>
      </div>
    </div>
  );
};

const Settings = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [businessName, setBusinessName] = useState("");
  const [businessPhone, setBusinessPhone] = useState("");
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState("1");
  const [vatRate, setVatRate] = useState("21");
  const [bankHolder, setBankHolder] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankIban, setBankIban] = useState("");
  const [bankBic, setBankBic] = useState("");
  const [workshopName, setWorkshopName] = useState("");
  const [workshopPhone, setWorkshopPhone] = useState("");
  const [publicUrl, setPublicUrl] = useState("");
  const [savingWorkshop, setSavingWorkshop] = useState(false);
  const [colorOptions, setColorOptions] = useState([]);
  // Quick filter for the screen-types list. Matches both label and key, case-insensitive.
  const [search, setSearch] = useState("");
  // Track which screen-type cards are collapsed. Default: all collapsed once
  // types load, so the screen is short and editing one card at a time is easy.
  // `null` = uninitialised (still loading); a Set with all keys = all collapsed.
  const [collapsed, setCollapsed] = useState(null);

  const toggleCollapsed = (key) => {
    setCollapsed((prev) => {
      const next = new Set(prev || []);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const collapseAll = () => setCollapsed(new Set(types.map((t) => t.key)));
  const expandAll = () => setCollapsed(new Set());
  const allCollapsed =
    types.length > 0 && collapsed && types.every((t) => collapsed.has(t.key));

  const load = async () => {
    setLoading(true);
    try {
      const [t, app, colors] = await Promise.all([
        listScreenTypes(),
        getAppSettings(),
        listColorOptions(),
      ]);
      setTypes(t);
      setColorOptions(colors || []);
      // First load: collapse all screen-type cards so the page is scannable.
      // Subsequent reloads (after Save) preserve whatever the user had open.
      setCollapsed((prev) => prev || new Set(t.map((x) => x.key)));
      setBusinessName(app.business_name || "");
      setBusinessPhone(app.business_phone || "");
      setNextInvoiceNumber(String(app.next_invoice_number ?? 1));
      setVatRate(String(app.vat_rate ?? 21));
      setBankHolder(app.bank_account_holder || "");
      setBankName(app.bank_name || "");
      setBankIban(app.bank_iban || "");
      setBankBic(app.bank_bic || "");
      setWorkshopName(app.workshop_name || "");
      setWorkshopPhone(app.workshop_phone || "");
      setPublicUrl(app.public_url || "");
      const d = {};
      t.forEach((x) => {
        d[x.key] = {
          tiers: (x.tiers || []).map((tier) => ({
            width_mm: String(tier.width_mm),
            height_mm:
              tier.height_mm !== undefined && tier.height_mm !== null
                ? String(tier.height_mm)
                : "",
            price: String(tier.price),
          })),
          cut_pieces: (x.cut_pieces || []).map((p) => ({
            label: p.label,
            qty: String(p.qty),
            source: p.source,
            offset_mm: String(p.offset_mm),
          })),
        };
      });
      setDrafts(d);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveWorkshop = async () => {
    const num = parseInt(nextInvoiceNumber, 10);
    if (!Number.isFinite(num) || num < 1) {
      toast.error("Next invoice number must be at least 1");
      return;
    }
    const vat = parseFloat(vatRate);
    if (!Number.isFinite(vat) || vat < 0 || vat > 100) {
      toast.error("IVA must be 0–100");
      return;
    }
    setSavingWorkshop(true);
    try {
      await updateAppSettings({
        business_name: businessName.trim(),
        business_phone: businessPhone.trim(),
        next_invoice_number: num,
        vat_rate: vat,
        bank_account_holder: bankHolder.trim(),
        bank_name: bankName.trim(),
        bank_iban: bankIban.trim(),
        bank_bic: bankBic.trim(),
        workshop_name: workshopName.trim(),
        workshop_phone: workshopPhone.trim(),
        public_url: publicUrl.trim().replace(/\/+$/, ""),
      });
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingWorkshop(false);
    }
  };

  const updateDraft = (key, updater) =>
    setDrafts((d) => ({ ...d, [key]: updater(d[key] || emptyDraft()) }));

  const addTier = (key) =>
    updateDraft(key, (dk) => ({ ...dk, tiers: [...(dk.tiers || []), newTier()] }));
  // Sliding-window helper: add a "Square" tier — when user fills the width,
  // height auto-fills to the same value (so a 300mm square matches up to 300mm).
  const addSquareTier = (key) =>
    updateDraft(key, (dk) => ({
      ...dk,
      tiers: [
        ...(dk.tiers || []),
        { width_mm: "", height_mm: "", price: "", _kind: "sq" },
      ],
    }));
  // Sliding-window helper: add a "Rect" tier with a sensible tall default (1500mm).
  const addRectTier = (key) =>
    updateDraft(key, (dk) => ({
      ...dk,
      tiers: [
        ...(dk.tiers || []),
        { width_mm: "", height_mm: "1500", price: "", _kind: "rect" },
      ],
    }));
  // Bulk template matching the partner's full price sheet:
  //   • 0.30 → 1.40m: each width gets a Square row + a Rect (≤1.5m tall) row
  //   • 1.50m: Square row only (the partner's sheet has no Rect price at 1.50m)
  // Total = 12 widths × 2 - 1 (no 1500-rect) = 23 ready-to-fill rows.
  const generateSqRectGrid = (key) => {
    if (
      !window.confirm(
        "Add 23 starter rows (0.30m → 1.50m, square + rect) matching the partner price sheet? Existing tiers will be kept."
      )
    )
      return;
    const widthsBoth = [300, 400, 500, 600, 700, 800, 900, 1000, 1100, 1200, 1300, 1400];
    const rows = [];
    widthsBoth.forEach((w) => {
      rows.push({ width_mm: String(w), height_mm: String(w), price: "", _kind: "sq" });
      rows.push({ width_mm: String(w), height_mm: "1500", price: "", _kind: "rect" });
    });
    // 1500 wide: square only (no rect price on the sheet)
    rows.push({ width_mm: "1500", height_mm: "1500", price: "", _kind: "sq" });
    updateDraft(key, (dk) => ({ ...dk, tiers: [...(dk.tiers || []), ...rows] }));
  };
  const removeTier = (key, idx) =>
    updateDraft(key, (dk) => ({
      ...dk,
      tiers: (dk.tiers || []).filter((_, i) => i !== idx),
    }));
  const updateTier = (key, idx, patch) =>
    updateDraft(key, (dk) => {
      const tiers = (dk.tiers || []).map((t, i) => {
        if (i !== idx) return t;
        const next = { ...t, ...patch };
        // Square tiers: keep height in lock-step with width as the user types
        if (next._kind === "sq" && "width_mm" in patch) {
          next.height_mm = patch.width_mm;
        }
        return next;
      });
      return { ...dk, tiers };
    });

  const addPiece = (key) =>
    updateDraft(key, (dk) => ({
      ...dk,
      cut_pieces: [...(dk.cut_pieces || []), newPiece()],
    }));
  const removePiece = (key, idx) =>
    updateDraft(key, (dk) => ({
      ...dk,
      cut_pieces: (dk.cut_pieces || []).filter((_, i) => i !== idx),
    }));
  const updatePiece = (key, idx, patch) =>
    updateDraft(key, (dk) => ({
      ...dk,
      cut_pieces: (dk.cut_pieces || []).map((p, i) =>
        i === idx ? { ...p, ...patch } : p
      ),
    }));

  const buildPayload = (key) => {
    const d = drafts[key] || emptyDraft();
    return {
      tiers: (d.tiers || [])
        .map((t) => {
          const out = { width_mm: Number(t.width_mm), price: Number(t.price) };
          const h = Number(t.height_mm);
          if (Number.isFinite(h) && h > 0) out.height_mm = h;
          return out;
        })
        .filter((t) => t.width_mm > 0),
      cut_pieces: (d.cut_pieces || [])
        .map((p) => ({
          label: (p.label || "").trim(),
          qty: Math.max(1, Number(p.qty) || 1),
          source: p.source || "width",
          offset_mm: Number(p.offset_mm) || 0,
        }))
        .filter((p) => p.label),
    };
  };

  const saveOne = async (key) => {
    try {
      await updateScreenType(key, buildPayload(key));
      toast.success("Saved");
      await load();
    } catch {
      toast.error("Save failed");
    }
  };

  // Copy the current tier list to the other 4 sliding-window variants.
  // Useful because Nick's price sheet uses the same Sq+Rect grid across every
  // sliding-window track variant — saves typing the 25 prices five times.
  // Map a screen-type key to its "family" so the Copy-to-others button finds
  // the right siblings. All sliding-window variants share one family, all
  // patio-door (single + double) variants share another. Extend here as
  // future families are added.
  const familyOfKey = (key) => {
    if (!key) return null;
    if (key.startsWith("sliding_window")) return "sliding_window";
    if (key.startsWith("patio_door") || key.startsWith("double_patio_door"))
      return "patio_door";
    return null;
  };

  const copyTiersToSiblings = async (sourceKey) => {
    const family = familyOfKey(sourceKey);
    if (!family) {
      toast.error("This screen type has no related variants to copy to");
      return;
    }
    const siblings = (types || []).filter(
      (t) => familyOfKey(t.key) === family && t.key !== sourceKey
    );
    if (siblings.length === 0) {
      toast.info("No other variants to copy to in this family");
      return;
    }
    const payload = buildPayload(sourceKey);
    if (!payload.tiers || payload.tiers.length === 0) {
      toast.error("No tiers on this card to copy. Add some first.");
      return;
    }
    const list = siblings.map((s) => `• ${s.label}`).join("\n");
    if (
      !window.confirm(
        `Copy ${payload.tiers.length} tier${payload.tiers.length === 1 ? "" : "s"} to:\n\n${list}\n\nThis OVERWRITES any existing tiers on those types. Cut recipes (15mm / 20mm / side tracks) are NOT touched — each variant keeps its own recipe.`
      )
    )
      return;
    try {
      await Promise.all(
        siblings.map((s) =>
          // Send tiers only — leave each variant's cut_pieces (track recipes) alone
          updateScreenType(s.key, { tiers: payload.tiers })
        )
      );
      await load();
      toast.success(
        `Copied to ${siblings.length} other ${family === "sliding_window" ? "sliding" : "patio"} type${siblings.length === 1 ? "" : "s"}`
      );
    } catch {
      toast.error("Copy failed");
    }
  };

  const deleteOne = async (key, label) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${label}"?\n\nThis cannot be undone. Existing quotes that use this type will keep their data but the type will no longer appear in dropdowns.`
    );
    if (!confirmed) return;
    try {
      await deleteScreenType(key);
      toast.success(`Deleted "${label}"`);
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const renameOne = async (key, currentLabel) => {
    const input = window.prompt("Rename screen type:", currentLabel);
    if (input === null) return;
    const newLabel = input.trim();
    if (!newLabel || newLabel === currentLabel) return;
    try {
      await updateScreenType(key, { ...buildPayload(key), label: newLabel });
      toast.success("Renamed");
      await load();
    } catch {
      toast.error("Rename failed");
    }
  };

  const addNew = async () => {
    const input = window.prompt(
      "Name for the new screen type (e.g. 'Fly screen 25mm'):"
    );
    if (input === null) return;
    const label = input.trim();
    if (!label) return;
    try {
      const created = await createScreenType(label);
      toast.success(`Added "${created.label}"`);
      await load();
    } catch {
      toast.error("Create failed");
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await Promise.all(
        types.map((t) => updateScreenType(t.key, buildPayload(t.key)))
      );
      toast.success("All settings saved");
      await load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 font-mono text-sm text-zinc-500">Loading...</div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <BackupReminder />
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-xs">Module 04 / Configuration</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
            PRICING & CUT RECIPES
          </h1>
          <p className="mt-3 text-sm text-zinc-600 font-mono max-w-2xl">
            For each screen type: set size/price tiers (round-up by width) and
            the production cut recipe (list of pieces with qty and formula).
            Formulas: <span className="text-[#005bea]">length = source + offset</span>.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={allCollapsed ? expandAll : collapseAll}
            className="btn-sharp secondary"
            data-testid="toggle-all-screen-types-btn"
          >
            {allCollapsed ? (
              <>
                <ChevronDown size={14} /> Expand all
              </>
            ) : (
              <>
                <ChevronRight size={14} /> Collapse all
              </>
            )}
          </button>
          <button
            onClick={addNew}
            className="btn-sharp secondary"
            data-testid="add-screen-type-btn"
          >
            <Plus size={14} /> New screen type
          </button>
          <button
            onClick={saveAll}
            disabled={saving}
            className="btn-sharp primary"
            data-testid="save-all-pricing-btn"
          >
            <Save size={14} /> {saving ? "Saving..." : "Save all"}
          </button>
        </div>
      </div>

      <div className="space-y-8" data-testid="settings-list">
        {/* Business contact */}
        <div className="border border-zinc-200" data-testid="business-contact">
          <div className="flex items-center justify-between border-b border-zinc-200 bg-[#f4f4f5] px-5 py-3">
            <div className="flex items-center gap-3">
              <Building2 size={18} />
              <div>
                <div className="font-display text-xl font-black tracking-tight">
                  Your Business
                </div>
                <div className="label-xs mt-1">
                  Shown at the top of customer quotes (WhatsApp & PDF)
                </div>
              </div>
            </div>
            <button
              onClick={saveWorkshop}
              disabled={savingWorkshop}
              className="text-xs uppercase tracking-[0.18em] font-semibold text-[#005bea] hover:text-[#0047c2] inline-flex items-center gap-1"
              data-testid="save-business-btn"
            >
              <Save size={13} /> {savingWorkshop ? "Saving..." : "Save"}
            </button>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="label-xs mb-1">Business name</div>
              <input
                className="input-sharp"
                placeholder="e.g. MosquitoNick"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                data-testid="business-name-input"
              />
            </div>
            <div>
              <div className="label-xs mb-1">Your phone (optional)</div>
              <input
                className="input-sharp tabular-nums"
                placeholder="e.g. +34612345678"
                value={businessPhone}
                onChange={(e) => setBusinessPhone(e.target.value)}
                data-testid="business-phone-input"
              />
            </div>
            <div className="sm:col-span-2">
              <div className="label-xs mb-1">Public URL (optional)</div>
              <input
                className="input-sharp"
                placeholder="https://app.mosquitonick.ws"
                value={publicUrl}
                onChange={(e) => setPublicUrl(e.target.value)}
                data-testid="public-url-input"
              />
              <div className="font-mono text-[10px] text-zinc-500 mt-1">
                When set, all customer-facing share links (workshop, day plan)
                use this domain — even if you happen to open the app from a
                preview URL. Leave blank to fall back to the current browser
                origin.
              </div>
            </div>
            <div>
              <div className="label-xs mb-1">Next invoice number</div>
              <input
                type="number"
                min="1"
                step="1"
                className="input-sharp tabular-nums"
                placeholder="1"
                value={nextInvoiceNumber}
                onChange={(e) => setNextInvoiceNumber(e.target.value)}
                data-testid="next-invoice-number-input"
              />
              <div className="font-mono text-[10px] text-zinc-500 mt-1">
                Next invoice will be issued as <b>INV-{(parseInt(nextInvoiceNumber, 10) || 1).toString().padStart(4, "0")}</b>. Counter auto-advances.
              </div>
            </div>
            <div>
              <div className="label-xs mb-1">IVA % (set 0 to disable)</div>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                className="input-sharp tabular-nums"
                placeholder="21"
                value={vatRate}
                onChange={(e) => setVatRate(e.target.value)}
                data-testid="vat-rate-input"
              />
            </div>
            <div className="sm:col-span-2 mt-2 pt-3 border-t border-zinc-100">
              <div className="label-xs mb-2">Bank transfer details (printed on invoices)</div>
              {(() => {
                const missing = [
                  !bankHolder.trim() && "Account holder",
                  !bankName.trim() && "Bank name",
                  !bankIban.trim() && "IBAN",
                  !bankBic.trim() && "BIC / SWIFT",
                ].filter(Boolean);
                if (missing.length === 0) return null;
                return (
                  <div
                    className="mb-3 flex items-start gap-2 border border-[#f59e0b] bg-[#fffbeb] p-3"
                    data-testid="bank-missing-warning"
                  >
                    <AlertTriangle
                      size={14}
                      className="text-[#b45309] flex-none mt-0.5"
                    />
                    <div className="text-[12px] leading-snug text-[#78350f]">
                      <span className="font-semibold">
                        {missing.length === 4
                          ? "Bank details not set"
                          : `${missing.length} field${missing.length === 1 ? "" : "s"} missing`}
                      </span>{" "}
                      — invoices will print incomplete bank info. Add{" "}
                      <span className="font-semibold">{missing.join(", ")}</span> below
                      and tap Save.
                    </div>
                  </div>
                );
              })()}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="label-xs mb-1">Account holder</div>
                  <input
                    className="input-sharp"
                    placeholder="e.g. Nickolas Earl Hobbs-Ainley"
                    value={bankHolder}
                    onChange={(e) => setBankHolder(e.target.value)}
                    data-testid="bank-holder-input"
                  />
                </div>
                <div>
                  <div className="label-xs mb-1">Bank name</div>
                  <input
                    className="input-sharp"
                    placeholder="e.g. Sabadell Elviria"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    data-testid="bank-name-input"
                  />
                </div>
                <div>
                  <div className="label-xs mb-1">IBAN</div>
                  <input
                    className="input-sharp tabular-nums"
                    placeholder="ES00 0000 0000 0000 0000 0000"
                    value={bankIban}
                    onChange={(e) => setBankIban(e.target.value)}
                    data-testid="bank-iban-input"
                  />
                </div>
                <div>
                  <div className="label-xs mb-1">BIC / SWIFT</div>
                  <input
                    className="input-sharp tabular-nums"
                    placeholder="e.g. BSABESBB"
                    value={bankBic}
                    onChange={(e) => setBankBic(e.target.value)}
                    data-testid="bank-bic-input"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Workshop contact */}
        <div className="border border-zinc-200" data-testid="workshop-contact">
          <div className="flex items-center justify-between border-b border-zinc-200 bg-[#f4f4f5] px-5 py-3">
            <div className="flex items-center gap-3">
              <Factory size={18} />
              <div>
                <div className="font-display text-xl font-black tracking-tight">
                  Workshop Contact
                </div>
                <div className="label-xs mt-1">
                  Used by the "Send to workshop" button on Cut Lists
                </div>
              </div>
            </div>
            <button
              onClick={saveWorkshop}
              disabled={savingWorkshop}
              className="text-xs uppercase tracking-[0.18em] font-semibold text-[#005bea] hover:text-[#0047c2] inline-flex items-center gap-1"
              data-testid="save-workshop-btn"
            >
              <Save size={13} /> {savingWorkshop ? "Saving..." : "Save"}
            </button>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="label-xs mb-1">Workshop guy's name</div>
              <input
                className="input-sharp"
                placeholder="e.g. Carlos"
                value={workshopName}
                onChange={(e) => setWorkshopName(e.target.value)}
                data-testid="workshop-name-input"
              />
            </div>
            <div>
              <div className="label-xs mb-1">WhatsApp number (with country code)</div>
              <input
                className="input-sharp tabular-nums"
                placeholder="e.g. +34612345678"
                value={workshopPhone}
                onChange={(e) => setWorkshopPhone(e.target.value)}
                data-testid="workshop-phone-input"
              />
            </div>
          </div>
        </div>

        {/* Colour pricing options */}
        <ColorOptionsCard
          options={colorOptions}
          onChange={setColorOptions}
        />

        {/* Quick filter: shrinks 17 cards into just the ones you care about right now */}
        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter screen types — try 'patio' or 'pleated'…"
            className="w-full border border-zinc-200 pl-9 pr-9 py-2.5 text-sm font-mono focus:outline-none focus:border-[#09090b]"
            data-testid="screen-types-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-[#09090b] p-1"
              title="Clear search"
              data-testid="screen-types-search-clear"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {(() => {
          const q = search.trim().toLowerCase();
          const visible = q
            ? types.filter(
                (t) =>
                  t.label.toLowerCase().includes(q) ||
                  t.key.toLowerCase().includes(q)
              )
            : types;
          if (q && visible.length === 0) {
            return (
              <div className="border border-zinc-200 p-6 text-sm text-zinc-500 font-mono text-center">
                No screen types match "{search}". Clear the filter or check spelling.
              </div>
            );
          }
          return visible.map((t) => {
          const draft = drafts[t.key] || emptyDraft();
          const tierRows = draft.tiers || [];
          const pieceRows = draft.cut_pieces || [];
          const isCollapsed = collapsed ? collapsed.has(t.key) : false;
          // Sliding-window family gets an optional max-height column on each tier
          const supportsHeightTier = (t.key || "").startsWith("sliding_window");
          return (
            <div
              key={t.key}
              className="border border-zinc-200"
              data-testid={`screen-type-row-${t.key}`}
            >
              <div className="flex items-center justify-between border-b border-zinc-200 bg-[#f4f4f5] px-3 sm:px-5 py-3 gap-2 sm:gap-4">
                <button
                  type="button"
                  onClick={() => toggleCollapsed(t.key)}
                  className="text-zinc-400 hover:text-[#09090b] shrink-0 -ml-1 p-1"
                  title={isCollapsed ? "Expand" : "Collapse"}
                  data-testid={`toggle-${t.key}`}
                >
                  {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                </button>
                <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                  <div className="w-[80px] sm:w-[120px] shrink-0" data-testid={`sketch-${t.key}`}>
                    <ScreenSketch typeKey={t.key} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => renameOne(t.key, t.label)}
                      data-testid={`rename-${t.key}`}
                      className="group inline-flex items-start gap-1.5 text-left hover:text-[#005bea] transition-colors"
                      title="Click to rename"
                    >
                      <span className="font-display text-base sm:text-xl font-black tracking-tight leading-tight">
                        {t.label}
                      </span>
                      <PencilLine
                        size={12}
                        className="mt-1 text-zinc-300 group-hover:text-[#005bea] flex-none"
                      />
                    </button>
                    <div className="label-xs mt-1">
                      {tierRows.length} tier{tierRows.length === 1 ? "" : "s"} ·{" "}
                      {pieceRows.length} cut piece
                      {pieceRows.length === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => saveOne(t.key)}
                    className="text-xs uppercase tracking-[0.18em] font-semibold text-[#005bea] hover:text-[#0047c2]"
                    data-testid={`save-${t.key}`}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => deleteOne(t.key, t.label)}
                    className="text-zinc-400 hover:text-[#ff5722] p-1"
                    title={`Delete ${t.label}`}
                    data-testid={`delete-${t.key}`}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              {!isCollapsed && (<>
              {/* Pricing Tiers */}
              <div className="border-b border-zinc-200">
                <div className="px-5 py-2 bg-white flex items-center justify-between gap-2 flex-wrap">
                  <div className="label-xs">Pricing Tiers · €/size</div>
                  {supportsHeightTier ? (
                    <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                      <button
                        onClick={() => addSquareTier(t.key)}
                        className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#005bea] hover:text-[#0047c2] inline-flex items-center gap-1"
                        data-testid={`add-square-tier-${t.key}`}
                        title="Add a square-shape tier (height = width)"
                      >
                        <Plus size={12} /> Square
                      </button>
                      <button
                        onClick={() => addRectTier(t.key)}
                        className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#005bea] hover:text-[#0047c2] inline-flex items-center gap-1"
                        data-testid={`add-rect-tier-${t.key}`}
                        title="Add a rectangular (taller) tier — defaults to height ≤ 1500mm"
                      >
                        <Plus size={12} /> Rect
                      </button>
                      <button
                        onClick={() => generateSqRectGrid(t.key)}
                        className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500 hover:text-[#09090b] inline-flex items-center gap-1"
                        data-testid={`generate-grid-${t.key}`}
                        title="Pre-fill 8 widths (0.30→1.00m) × Square + Rect — 16 rows"
                      >
                        <Plus size={12} /> Generate grid
                      </button>
                      <button
                        onClick={() => copyTiersToSiblings(t.key)}
                        className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500 hover:text-[#09090b] inline-flex items-center gap-1"
                        data-testid={`copy-tiers-${t.key}`}
                        title="Copy these tiers to the other sliding-window variants"
                      >
                        <Copy size={12} /> Copy to others
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                      <button
                        onClick={() => addTier(t.key)}
                        className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#005bea] hover:text-[#0047c2] inline-flex items-center gap-1"
                        data-testid={`add-tier-${t.key}`}
                      >
                        <Plus size={12} /> Add tier
                      </button>
                      {familyOfKey(t.key) && (
                        <button
                          onClick={() => copyTiersToSiblings(t.key)}
                          className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500 hover:text-[#09090b] inline-flex items-center gap-1"
                          data-testid={`copy-tiers-${t.key}`}
                          title="Copy these tiers to the other variants in this family (cut recipes are not touched)"
                        >
                          <Copy size={12} /> Copy to others
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {tierRows.length === 0 ? (
                  <div className="px-5 pb-4 font-mono text-[11px] text-zinc-500">
                    No pricing tiers — room price will be €0.
                  </div>
                ) : (
                  <>
                    <div
                      className={`hidden sm:grid ${supportsHeightTier ? "grid-cols-[1fr_1fr_1fr_40px]" : "grid-cols-[1fr_1fr_40px]"} px-5 py-1 bg-white`}
                    >
                      <div className="label-xs">Width up to (mm)</div>
                      {supportsHeightTier && (
                        <div className="label-xs">Height up to (mm)</div>
                      )}
                      <div className="label-xs">Price (€)</div>
                      <div />
                    </div>
                    {tierRows.map((tier, i) => {
                      // Infer kind for legacy/back-compat rows (set on first paint)
                      const inferred =
                        tier._kind ||
                        (supportsHeightTier && tier.height_mm
                          ? Number(tier.height_mm) === Number(tier.width_mm)
                            ? "sq"
                            : Number(tier.height_mm) > Number(tier.width_mm)
                              ? "rect"
                              : ""
                          : "");
                      const badge =
                        supportsHeightTier && inferred === "sq"
                          ? { text: "SQ", cls: "bg-[#09090b] text-white" }
                          : supportsHeightTier && inferred === "rect"
                            ? { text: "RECT", cls: "bg-[#005bea] text-white" }
                            : null;
                      return (
                      <div
                        key={i}
                        className={`grid ${supportsHeightTier ? "grid-cols-[1fr_1fr_1fr_32px]" : "grid-cols-[1fr_1fr_32px]"} gap-2 sm:gap-3 items-center px-3 sm:px-5 py-2 border-t border-zinc-100`}
                        data-testid={`tier-${t.key}-${i}`}
                      >
                        <div className="relative flex items-center gap-1">
                          {badge && (
                            <span
                              className={`text-[9px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 ${badge.cls}`}
                              data-testid={`tier-kind-badge-${t.key}-${i}`}
                            >
                              {badge.text}
                            </span>
                          )}
                          <input
                            type="number"
                            step="1"
                            className="input-sharp tabular-nums flex-1 min-w-0"
                            placeholder="Width up to (mm)"
                            value={tier.width_mm}
                            onChange={(e) =>
                              updateTier(t.key, i, { width_mm: e.target.value })
                            }
                            data-testid={`tier-width-${t.key}-${i}`}
                          />
                        </div>
                        {supportsHeightTier && (
                          <input
                            type="number"
                            step="1"
                            className="input-sharp tabular-nums"
                            placeholder="Any height"
                            title="Optional. Leave blank if this tier applies regardless of height."
                            value={tier.height_mm || ""}
                            onChange={(e) =>
                              updateTier(t.key, i, { height_mm: e.target.value })
                            }
                            data-testid={`tier-height-${t.key}-${i}`}
                          />
                        )}
                        <input
                          type="number"
                          step="0.01"
                          className="input-sharp tabular-nums"
                          placeholder="Price €"
                          value={tier.price}
                          onChange={(e) =>
                            updateTier(t.key, i, { price: e.target.value })
                          }
                          data-testid={`tier-price-${t.key}-${i}`}
                        />
                        <button
                          onClick={() => removeTier(t.key, i)}
                          className="text-zinc-400 hover:text-[#ff5722]"
                          data-testid={`tier-delete-${t.key}-${i}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      );
                    })}
                    {supportsHeightTier && (
                      <div className="px-5 py-2 bg-white text-[11px] font-mono text-zinc-500 border-t border-zinc-100">
                        Tip: leave Height blank to match any height. Add multiple
                        tiers to charge more for taller windows at the same width.
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Cut Recipe */}
              <div>
                <div className="px-5 py-2 bg-white flex items-center justify-between">
                  <div className="label-xs">Cut Recipe · Production pieces</div>
                  <button
                    onClick={() => addPiece(t.key)}
                    className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#005bea] hover:text-[#0047c2] inline-flex items-center gap-1"
                    data-testid={`add-piece-${t.key}`}
                  >
                    <Plus size={12} /> Add piece
                  </button>
                </div>
                {pieceRows.length > 0 ? (
                  <div
                    className="mx-3 sm:mx-5 mb-2 px-3 py-2 bg-amber-50 border border-amber-300 font-mono text-[11px] text-amber-900 leading-relaxed"
                    data-testid={`source-tip-${t.key}`}
                  >
                    <b>Tip:</b> for any cut piece that should follow the
                    customer-typed <b>Track</b> dimension (e.g. a
                    "Bottom track 15mm" sill), set its <b>Source</b> to
                    "<b>track width</b>". Pieces left on "opening width"
                    will use the room's W instead — which is why some sliding
                    windows ignored the typed Track value.
                  </div>
                ) : null}
                {pieceRows.length === 0 ? (
                  <div className="px-5 pb-4 font-mono text-[11px] text-zinc-500">
                    No cut pieces — production cut list will be empty.
                  </div>
                ) : (
                  <>
                    <div className="hidden sm:grid grid-cols-[1.6fr_60px_1fr_1fr_40px] gap-2 px-5 py-1 bg-white">
                      <div className="label-xs">Piece name</div>
                      <div className="label-xs">Qty</div>
                      <div className="label-xs">Source</div>
                      <div className="label-xs">Offset (mm)</div>
                      <div />
                    </div>
                    {pieceRows.map((p, i) => (
                      <div
                        key={i}
                        className="px-3 sm:px-5 py-3 border-t border-zinc-100"
                        data-testid={`piece-${t.key}-${i}`}
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-[1.6fr_60px_1fr_1fr_32px] gap-2 sm:items-center">
                          <input
                            className="input-sharp"
                            placeholder="Piece name (e.g. Top/Bottom rail)"
                            value={p.label}
                            onChange={(e) =>
                              updatePiece(t.key, i, { label: e.target.value })
                            }
                            data-testid={`piece-label-${t.key}-${i}`}
                          />
                          <div className="grid grid-cols-[60px_1fr_1fr_32px] sm:contents gap-2 items-center">
                            <input
                              type="number"
                              min="1"
                              step="1"
                              className="input-sharp tabular-nums text-center"
                              value={p.qty}
                              onChange={(e) =>
                                updatePiece(t.key, i, { qty: e.target.value })
                              }
                              data-testid={`piece-qty-${t.key}-${i}`}
                            />
                            <select
                              className="input-sharp"
                              value={p.source}
                              onChange={(e) =>
                                updatePiece(t.key, i, { source: e.target.value })
                              }
                              data-testid={`piece-source-${t.key}-${i}`}
                            >
                              {SOURCES.map((s) => (
                                <option key={s.value} value={s.value}>
                                  {s.label}
                                </option>
                              ))}
                            </select>
                            <input
                              type="number"
                              step="0.1"
                              className="input-sharp tabular-nums"
                              placeholder="Offset"
                              value={p.offset_mm}
                              onChange={(e) =>
                                updatePiece(t.key, i, { offset_mm: e.target.value })
                              }
                              data-testid={`piece-offset-${t.key}-${i}`}
                            />
                            <button
                              onClick={() => removePiece(t.key, i)}
                              className="text-zinc-400 hover:text-[#ff5722] flex items-center justify-center"
                              data-testid={`piece-delete-${t.key}-${i}`}
                              aria-label="Delete piece"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
              </>)}
            </div>
          );
        });
        })()}
      </div>

      <BackupPanel />
    </div>
  );
};

// ---- Backup & Restore panel ----
const BackupPanel = () => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const { lastAt, stampNow } = useBackupReminder();

  useEffect(() => {
    getBackupPreview()
      .then(setPreview)
      .catch(() => setPreview(null));
  }, []);

  const handleDownload = async () => {
    setBusy(true);
    try {
      const blob = await downloadBackupBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `mosquitonick_backup_${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      stampNow();
      toast.success("Backup downloaded — keep it somewhere safe");
    } catch {
      toast.error("Backup download failed");
    } finally {
      setBusy(false);
    }
  };

  const handleRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be picked again
    if (!file) return;
    let payload;
    try {
      payload = JSON.parse(await file.text());
    } catch {
      toast.error("That file isn't a valid backup JSON");
      return;
    }
    if (!payload || typeof payload !== "object" || !payload.collections) {
      toast.error("Backup file looks malformed");
      return;
    }
    const counts = Object.entries(payload.collections || {})
      .map(([k, v]) => `${(v || []).length} ${k}`)
      .join(", ");
    if (!window.confirm(
      `Restore from ${file.name}?\n\nContents: ${counts}.\n\nMerge mode — existing items with the same id will be overwritten; new items will be added; nothing else is touched.`
    )) {
      return;
    }
    setBusy(true);
    try {
      const res = await restoreBackup(payload, "merge");
      const summaryText = Object.entries(res.summary || {})
        .map(([k, v]) => `${k}: +${v.inserted} new / ~${v.modified} updated`)
        .join(" · ");
      toast.success(`Restored — ${summaryText}`);
      // Reload preview counts
      getBackupPreview().then(setPreview).catch(() => {});
    } catch (err) {
      const msg = err?.response?.data?.detail || "Restore failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-12 border-t border-zinc-200 pt-8">
      <div className="flex items-center gap-2 mb-1">
        <Database size={14} className="text-zinc-500" />
        <div className="label-xs">Backup & Restore</div>
      </div>
      <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tighter mb-2">
        Never lose your data.
      </h2>
      <p className="text-zinc-600 text-sm max-w-xl mb-6">
        Download a single JSON file with every quote, customer, screen-type
        recipe and setting. Save it to iCloud / Google Drive / email it to
        yourself — anywhere safe. If anything ever goes wrong, drop the file
        back here to restore.
      </p>

      {preview ? (
        <div className="grid grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 mb-6 max-w-md">
          <div className="bg-white p-3">
            <div className="label-xs">Quotes</div>
            <div
              className="font-display text-2xl font-black tracking-tighter tabular-nums"
              data-testid="backup-count-quotes"
            >
              {preview.collections?.quotes ?? 0}
            </div>
          </div>
          <div className="bg-white p-3">
            <div className="label-xs">Screen types</div>
            <div className="font-display text-2xl font-black tracking-tighter tabular-nums">
              {preview.collections?.screen_types ?? 0}
            </div>
          </div>
          <div className="bg-white p-3">
            <div className="label-xs">Settings</div>
            <div className="font-display text-2xl font-black tracking-tighter tabular-nums">
              {preview.collections?.app_settings ?? 0}
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleDownload}
          disabled={busy}
          data-testid="backup-download-btn"
          className="inline-flex items-center gap-2 bg-[#09090b] hover:bg-[#27272a] disabled:opacity-60 text-white px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold transition-colors"
        >
          <Download size={14} /> {busy ? "Working…" : "Download backup"}
        </button>

        <label
          className={`inline-flex items-center gap-2 border border-[#09090b] hover:bg-zinc-100 px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold cursor-pointer transition-colors ${
            busy ? "opacity-60 pointer-events-none" : ""
          }`}
          data-testid="backup-restore-label"
        >
          <Upload size={14} /> Restore from file
          <input
            type="file"
            accept="application/json,.json"
            onChange={handleRestoreFile}
            className="hidden"
            data-testid="backup-restore-input"
            disabled={busy}
          />
        </label>
      </div>
      <p className="text-[11px] text-zinc-500 mt-3 max-w-xl">
        Tip: download a backup once a week — takes 2 seconds. Even better, save
        it straight to a folder that auto-syncs to iCloud / Drive.
      </p>
      {lastAt ? (
        <p
          className="text-[11px] text-zinc-500 mt-2 font-mono"
          data-testid="backup-last-line"
        >
          Last backup: {new Date(lastAt).toLocaleString()}
        </p>
      ) : null}
    </div>
  );
};

// Tiny amber banner shown at the top of Settings when no backup has been
// taken in the last 14 days. Stores the timestamp in localStorage on the
// device — so a fresh browser will show the nudge until you download once.
const BackupReminder = () => {
  const { daysSince, overdue } = useBackupReminder();
  if (!overdue) return null;
  const message =
    daysSince === null
      ? "You haven't backed up your data yet — take 2 seconds to download a copy."
      : `Last backup was ${daysSince} days ago — download a fresh copy when you have a sec.`;
  const goToBackup = (e) => {
    e.preventDefault();
    document
      .querySelector('[data-testid="backup-download-btn"]')
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  return (
    <div
      className="mb-6 flex items-start gap-3 border border-[#f59e0b] bg-[#fffbeb] p-3"
      data-testid="backup-reminder"
    >
      <AlertTriangle
        size={14}
        className="text-[#b45309] flex-none mt-0.5"
      />
      <div className="text-[12px] leading-snug text-[#78350f] flex-1">
        <span className="font-semibold">Backup reminder.</span> {message}{" "}
        <a
          href="#backup"
          onClick={goToBackup}
          className="underline font-semibold hover:text-[#92400e]"
          data-testid="backup-reminder-link"
        >
          Jump to backup →
        </a>
      </div>
    </div>
  );
};

export default Settings;
